/**
 * 坐标计算工具函数
 * 用于计算 StudioCard 与地图 Icon 之间连接线的坐标和样式
 */

import { EChartsType } from 'echarts';

/**
 * 卡片位置类型
 */
export type TCardPosition = 'top' | 'left' | 'right' | 'bottom';

/**
 * 连接线样式配置
 */
export interface IConnectionLineStyle {
  left: number;
  top: number;
  width: number;
  height: number;
  borderTop?: string;
  borderRight?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRadius: string;
}

/**
 * 计算 StudioCard 红色圆点的绝对坐标
 * @param cardElement - StudioCard DOM 元素
 * @param position - 卡片位置（top/left/right）
 * @param containerElement - 容器元素（用于计算相对坐标）
 * @returns 圆点的绝对坐标 { x, y }
 */
export const calculateDotPosition = (
  containerElement: HTMLElement,
  hoveredStudioName: string,
): { x: number; y: number; } => {
  const imgEl = document.getElementById(hoveredStudioName);
  const { x = 0, y = 0 } = imgEl?.getBoundingClientRect() || {};

  const containerRect = containerElement.getBoundingClientRect();
  const dotX: number = x - containerRect.left;
  const dotY: number = y - containerRect.top;

  return { x: dotX, y: dotY };
};

/**
 * 获取地图 Studio Icon 的像素坐标
 * @param chartInstance - ECharts 实例
 * @param studioName - Studio 名称
 * @param containerElement - 容器元素
 * @returns Icon 的像素坐标 { x, y }，如果未找到则返回 null
 */
export const getIconCoordinates = (
  chartElement: HTMLDivElement,
  studioName: string,
  containerElement: HTMLElement,
  chartInstance: EChartsType | undefined | null,
): { x: number; y: number; symbolSize?: number; } | null => {
  try {
    // 获取图表配置
    const option = chartInstance?.getOption();
    if (!option?.series) {
      return null;
    }

    // 从 series 中找到对应的 studio 数据
    const series = Array.isArray(option.series)
      ? option.series
      : [option.series];

    let studioCoord: [number, number] | null = null;
    let symbolSize = 0;

    for (const serie of series) {
      if (serie.type === 'scatter' && serie.data) {
        const dataArray = Array.isArray(serie.data) ? serie.data : [serie.data];
        for (const item of dataArray) {
          if (item.studio?.name === studioName && item.value) {
            studioCoord = [item.value[0], item.value[1]];
            if (typeof (serie as any).symbolSize === 'number') {
              symbolSize = (serie as any).symbolSize;
            }
            break;
          }
        }
      }
      if (studioCoord) break;
    }

    if (!studioCoord) {
      return null;
    }

    // 将地理坐标转换为像素坐标
    const pixel = chartInstance?.convertToPixel({ geoIndex: 0 }, studioCoord);

    if (!pixel || pixel.length < 2) {
      return null;
    }

    const chartRect = chartElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();

    // 计算 icon 在容器坐标系中的位置
    // pixel 是 icon 在图表中的相对坐标（0,0 为图表左上角）
    // 需要加上图表相对于容器的偏移量
    // 图表相对于容器的偏移 = 图表在视口中的位置 - 容器在视口中的位置
    const offsetX = chartRect.left - containerRect.left;
    const offsetY = chartRect.top - containerRect.top;

    const iconX = offsetX + pixel[0];
    const iconY = offsetY + pixel[1];

    return {
      x: iconX,
      y: iconY,
      symbolSize: symbolSize / 2,
    };
  } catch (error) {
    console.warn(`Failed to get icon coordinates for ${studioName}:`, error);
    return null;
  }
};

/**
 * 根据卡片位置和坐标关系计算连接线样式
 * @param position - 卡片位置
 * @param dotX - 圆点 X 坐标
 * @param dotY - 圆点 Y 坐标
 * @param iconX - 图标 X 坐标
 * @param iconY - 图标 Y 坐标
 * @returns 连接线样式配置
 */
export const calculateConnectionLineStyle = (
  position: TCardPosition,
  dotX: number,
  dotY: number,
  iconX: number,
  iconY: number,
  symbolSize = 0,
): IConnectionLineStyle => {
  const borderStyle = '2px dashed rgba(50, 111, 240, 0.6)';
  let left: number;
  let top: number;
  let width: number;
  let height: number;
  let borderTop: string | undefined;
  let borderRight: string | undefined;
  let borderBottom: string | undefined;
  let borderLeft: string | undefined;
  let borderRadius: string;

  if (position === 'left') {
    // 卡片在左侧，圆点在右边缘
    if (dotY < iconY) {
      // 圆点在图标上方：使用 border-right + border-bottom（正 L）
      left = dotX + 10;
      top = dotY + 9;
      width = iconX - dotX - 10;
      height = iconY - dotY - 12 - symbolSize;
      borderRight = borderStyle;
      borderTop = borderStyle;
      borderRadius = '0 8px';
    } else {
      // 圆点在图标下方：使用 border-right + border-top（倒 L）
      left = dotX + 10;
      top = iconY + symbolSize + 2;
      width = iconX - dotX - 10;
      height = dotY - iconY + 10 - symbolSize;
      borderRight = borderStyle;
      borderBottom = borderStyle;
      borderRadius = '8px 0';
    }
  } else if (position === 'right') {
    // 卡片在右侧，圆点在左边缘
    if (dotY < iconY) {
      // 圆点在图标上方：使用 border-left + border-bottom（镜像正 L）
      left = iconX;
      top = dotY + 9;
      width = dotX - iconX + 10;
      height = iconY - dotY - 9 - symbolSize;
      borderLeft = borderStyle;
      borderTop = borderStyle;
      borderRadius = '8px 0';
    } else {
      // 圆点在图标下方：使用 border-left + border-top（镜像倒 L）
      left = iconX;
      top = iconY + symbolSize + 6;
      width = dotX - iconX + 10;
      height = dotY - iconY + 6 - symbolSize;
      borderLeft = borderStyle;
      borderBottom = borderStyle;
      borderRadius = '0 8px';
    }
  } else if (position === 'bottom') {
    // 卡片在下方，圆点在上边缘
    if (dotX < iconX) {
      // 圆点在图标左侧：使用 border-top
      left = dotX + 9;
      top = iconY;
      width = iconX - dotX - symbolSize - 9;
      height = dotY - iconY;
      borderTop = borderStyle;
      borderLeft = borderStyle;
      borderRadius = '8px 0';
    } else {
      // 圆点在图标右侧：使用 border-top
      left = iconX + symbolSize + 6;
      top = iconY;
      width = dotX - iconX - symbolSize + 6;
      height = dotY - iconY;
      borderTop = borderStyle;
      borderRight = borderStyle;
      borderRadius = '0 8px';
    }
  } else {
    // position === 'top'，卡片在上方，圆点在下边缘
    if (dotX < iconX) {
      // 圆点在图标左侧：使用 border-bottom + border-right（正 L）
      left = dotX + 9;
      top = dotY + 10;
      width = iconX - dotX - 9 - symbolSize;
      height = iconY - dotY - 10;
      borderBottom = borderStyle;
      borderLeft = borderStyle;
      borderRadius = '0 8px';
    } else {
      // 圆点在图标右侧：使用 border-bottom + border-left（镜像正 L）
      left = iconX + symbolSize;
      top = dotY + 10;
      width = dotX - iconX + 11 - symbolSize;
      height = iconY - dotY - 10;
      borderBottom = borderStyle;
      borderRight = borderStyle;
      borderRadius = '8px 0';
    }
  }

  return {
    left,
    top,
    width,
    height,
    borderTop,
    borderRight,
    borderBottom,
    borderLeft,
    borderRadius,
  };
};
